import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { DollarSign, ArrowLeft, FileText, CheckCircle2, AlertCircle, Search, Filter, RotateCcw, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tabella prezzi attività per Accordi Quadro
const ATTIVITA_PREZZI = {
  '1': { nome: 'Fattibilità preliminare da remoto per impianto Fotovoltaici < 500 kWp', costo: 70 },
  '2': { nome: 'Fattibilità preliminare da remoto per impianti fotovoltaici > 500 kWp o altri impianti', costo: 250 },
  '3': { nome: 'Fattibilità preliminare con sopralluogo', costo: 700 },
  '4': { nome: 'Progetto di fattibilità a corpo', costo: 2500 },
  '5': { nome: 'Studio illuminotecnico fino a 500 Lampade', costo: 400 },
  '6': { nome: 'Analisi di completezza e coerenza di progettazione eseguita da terzi', costo: 70 },
  '7': { nome: 'Stesura Business Plan su modello pre-impostato', costo: 50 },
  '8': { nome: 'Sopralluogo su impianti industriali/terziario', costo: 300 },
  '9': { nome: 'Verifica output preventivatore', costo: 25 },
};

// Funzione per ottenere il costo di un progetto
const getCostoProgetto = (progetto) => {
  if (progetto.attivita_accordo && ATTIVITA_PREZZI[progetto.attivita_accordo]) {
    return ATTIVITA_PREZZI[progetto.attivita_accordo].costo;
  }
  return progetto.importo_progetto || 0;
};

const FatturazionePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [ordini, setOrdini] = useState([]);
  const [progetti, setProgetti] = useState([]);  // Progetti da accordi quadro
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fatturaDialogOpen, setFatturaDialogOpen] = useState(false);
  const [selectedSAL, setSelectedSAL] = useState(null);
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [selectedProgetto, setSelectedProgetto] = useState(null);  // Per fatturazione progetti
  const [selectedGruppoAccordo, setSelectedGruppoAccordo] = useState(null);  // Per fatturazione accordi quadro
  const [importoManuale, setImportoManuale] = useState('');  // Importo manuale per accordi
  const [fatturaData, setFatturaData] = useState({
    numero_fattura: '',
    data_fattura: new Date().toISOString().split('T')[0],
    data_scadenza: '',
    note: ''
  });
  
  // Funzione per copiare testo negli appunti
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copiato!`);
    }).catch(() => {
      toast.error('Errore nella copia');
    });
  };
  
  // Funzione per calcolare la data di scadenza basata sui termini di pagamento
  const calcolaDataScadenza = (dataFattura, giorniPagamento, fatturaFineMese) => {
    if (!dataFattura) return '';
    
    const data = new Date(dataFattura);
    let dataPartenza = data;
    
    // Se "Fattura Fine Mese" è attivo, la data di partenza è l'ultimo giorno del mese
    if (fatturaFineMese) {
      const annoMese = new Date(data.getFullYear(), data.getMonth() + 1, 0); // Ultimo giorno del mese
      dataPartenza = annoMese;
    }
    
    // Aggiungi i giorni di pagamento
    const dataScadenza = new Date(dataPartenza);
    dataScadenza.setDate(dataScadenza.getDate() + (giorniPagamento || 30));
    
    return dataScadenza.toISOString().split('T')[0];
  };
  
  // Aggiorna automaticamente la data scadenza quando cambia la data fattura
  const handleDataFatturaChange = (newDataFattura) => {
    setFatturaData(prev => {
      const nuovaDataScadenza = selectedSAL 
        ? calcolaDataScadenza(
            newDataFattura, 
            selectedSAL.termini_pagamento_giorni || 30, 
            selectedSAL.fattura_fine_mese || false
          )
        : '';
      
      return { 
        ...prev, 
        data_fattura: newDataFattura,
        data_scadenza: nuovaDataScadenza
      };
    });
  };
  
  // Tab attiva: 'da_fatturare' o 'fatturate'
  const [activeTab, setActiveTab] = useState('da_fatturare');
  
  // Filtri per fatture emesse
  const [filters, setFilters] = useState({
    searchText: '',
    clienteId: '',
    dataInizio: '',
    dataFine: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchOrdini();
    fetchClienti();
    fetchProgetti();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchOrdini = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ordini-lavoro`, getAuthHeaders());
      setOrdini(response.data);
    } catch (error) {
      console.error('Errore caricamento ordini:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const fetchClienti = async () => {
    try {
      const response = await axios.get(`${API}/clienti`, getAuthHeaders());
      setClienti(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };
  
  const fetchProgetti = async () => {
    try {
      const response = await axios.get(`${API}/progetti`, getAuthHeaders());
      setProgetti(response.data);
    } catch (error) {
      console.error('Errore caricamento progetti:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // SAL da fatturare
  const getSALDaFatturare = () => {
    const salDaFatturare = [];
    ordini.forEach(ordine => {
      if (ordine.sal_list && ordine.sal_list.length > 0) {
        ordine.sal_list.forEach(sal => {
          if (sal.stato === 'da_fatturare') {
            salDaFatturare.push({
              ...sal,
              ordine: ordine
            });
          }
        });
      }
    });
    return salDaFatturare;
  };

  // SAL fatturati (fatture emesse)
  const getSALFatturati = () => {
    const salFatturati = [];
    ordini.forEach(ordine => {
      if (ordine.sal_list && ordine.sal_list.length > 0) {
        ordine.sal_list.forEach(sal => {
          if (sal.stato === 'fatturato') {
            salFatturati.push({
              ...sal,
              ordine: ordine,
              tipo: 'sal'  // Per distinguere dai progetti
            });
          }
        });
      }
    });
    return salFatturati;
  };
  
  // Progetti da accordi quadro - da fatturare, raggruppati per accordo+mese
  const getProgettiDaFatturare = () => {
    const progettiDaFatt = progetti.filter(p => p.stato_fatturazione === 'da_fatturare' && p.accordo_quadro_id);
    return progettiDaFatt;
  };
  
  // Raggruppa progetti per Accordo Quadro + Mese
  const getProgettiRaggruppatiPerAccordoMese = () => {
    const progettiDaFatt = getProgettiDaFatturare();
    const grouped = {};
    
    progettiDaFatt.forEach(progetto => {
      const dataFine = progetto.data_fine_prevista || progetto.data_fine_effettiva;
      let mese = 'Non assegnato';
      if (dataFine) {
        const date = new Date(dataFine);
        mese = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
      
      const key = `${progetto.accordo_quadro_id}_${mese}`;
      if (!grouped[key]) {
        grouped[key] = {
          accordo_quadro_id: progetto.accordo_quadro_id,
          accordo_quadro_numero: progetto.accordo_quadro_numero,
          codice_accordo_quadro: progetto.codice_accordo_quadro || '',
          cliente_nome: progetto.cliente_nome,
          mese: mese,
          progetti: [],
          importo_totale: 0,
          codice_oda: progetto.codice_oda || ''
        };
      }
      grouped[key].progetti.push(progetto);
      // Usa il costo dell'attività dalla tabella prezzi
      grouped[key].importo_totale += getCostoProgetto(progetto);
      // Aggiorna codice_accordo_quadro se non era ancora presente
      if (!grouped[key].codice_accordo_quadro && progetto.codice_accordo_quadro) {
        grouped[key].codice_accordo_quadro = progetto.codice_accordo_quadro;
      }
    });
    
    return Object.values(grouped);
  };
  
  // Progetti da accordi quadro - fatturati
  const getProgettiFatturati = () => {
    return progetti.filter(p => p.stato_fatturazione === 'fatturato' && p.accordo_quadro_id);
  };
  
  // Raggruppa progetti fatturati per Accordo + Mese (simile a da_fatturare)
  const getProgettiFatturatiRaggruppati = () => {
    const progettiFatturati = getProgettiFatturati();
    const grouped = {};
    
    progettiFatturati.forEach(progetto => {
      const dataFine = progetto.data_fine_prevista || progetto.data_fine_effettiva;
      let mese = 'Non assegnato';
      if (dataFine) {
        const date = new Date(dataFine);
        mese = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
      
      const key = `${progetto.accordo_quadro_id}_${mese}`;
      if (!grouped[key]) {
        grouped[key] = {
          tipo: 'accordo_quadro',
          accordo_quadro_id: progetto.accordo_quadro_id,
          accordo_quadro_numero: progetto.accordo_quadro_numero,
          codice_accordo_quadro: progetto.codice_accordo_quadro || '',
          cliente_nome: progetto.cliente_nome,
          mese: mese,
          progetti: [],
          importo_totale: 0,
          codice_oda: progetto.codice_oda || '',
          numero_fattura: progetto.numero_fattura || '',
          data_fattura: progetto.data_fattura || progetto.data_fatturazione || ''
        };
      }
      grouped[key].progetti.push(progetto);
      grouped[key].importo_totale += getCostoProgetto(progetto);
      if (!grouped[key].numero_fattura && progetto.numero_fattura) {
        grouped[key].numero_fattura = progetto.numero_fattura;
      }
      if (!grouped[key].data_fattura && (progetto.data_fattura || progetto.data_fatturazione)) {
        grouped[key].data_fattura = progetto.data_fattura || progetto.data_fatturazione;
      }
    });
    
    return Object.values(grouped);
  };

  // Filtra SAL fatturati + Progetti AQ fatturati
  const getFilteredFatture = () => {
    const salFatturati = getSALFatturati();
    const progettiAQFatturati = getProgettiFatturatiRaggruppati();
    
    // Converti progetti AQ in formato compatibile
    const aqItems = progettiAQFatturati.map(gruppo => ({
      tipo: 'accordo_quadro',
      ordine: {
        id: gruppo.accordo_quadro_id,
        numero_ordine: gruppo.accordo_quadro_numero,
        protocollo_ordine_lavoro: gruppo.codice_accordo_quadro, // AQ usa codice come protocollo
        cliente_nome: gruppo.cliente_nome,
        nome_progetto: `${gruppo.progetti.length} progett${gruppo.progetti.length === 1 ? 'o' : 'i'} - ${gruppo.mese === 'Non assegnato' ? gruppo.mese : new Date(gruppo.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`,
        codice_progetto_main_solution: gruppo.codice_accordo_quadro,
        codice_progetto_cliente: gruppo.codice_accordo_quadro,
        tipologia_lavorazione: 'Accordo Quadro',
        cliente_id: gruppo.progetti[0]?.cliente_id || ''
      },
      numero_sal: '-',
      importo: gruppo.importo_totale,
      riferimento_fattura: gruppo.codice_oda,
      numero_fattura: gruppo.numero_fattura,
      data_fatturazione: gruppo.data_fattura
    }));
    
    // Combina tutti gli items
    const allItems = [...salFatturati, ...aqItems];
    
    return allItems.filter(item => {
      // Filtro ricerca testuale
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesSearch = 
          (item.ordine.numero_ordine && item.ordine.numero_ordine.toLowerCase().includes(searchLower)) ||
          (item.ordine.protocollo_ordine_lavoro && item.ordine.protocollo_ordine_lavoro.toLowerCase().includes(searchLower)) ||
          (item.ordine.cliente_nome && item.ordine.cliente_nome.toLowerCase().includes(searchLower)) ||
          (item.ordine.nome_progetto && item.ordine.nome_progetto.toLowerCase().includes(searchLower)) ||
          (item.ordine.codice_progetto_main_solution && item.ordine.codice_progetto_main_solution.toLowerCase().includes(searchLower)) ||
          (item.ordine.codice_progetto_cliente && item.ordine.codice_progetto_cliente.toLowerCase().includes(searchLower)) ||
          (item.riferimento_fattura && item.riferimento_fattura.toLowerCase().includes(searchLower)) ||
          (item.numero_fattura && item.numero_fattura.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      
      // Filtro cliente
      if (filters.clienteId && item.ordine.cliente_id !== filters.clienteId) {
        return false;
      }
      
      // Filtro data fatturazione
      if (filters.dataInizio && item.data_fatturazione) {
        const dataFatt = new Date(item.data_fatturazione);
        const dataInizio = new Date(filters.dataInizio);
        if (dataFatt < dataInizio) return false;
      }
      
      if (filters.dataFine && item.data_fatturazione) {
        const dataFatt = new Date(item.data_fatturazione);
        const dataFine = new Date(filters.dataFine);
        if (dataFatt > dataFine) return false;
      }
      
      return true;
    });
  };
  
  // Conteggio totale fatture (SAL + AQ)
  const getTotaleFattureCount = () => {
    return getSALFatturati().length + getProgettiFatturatiRaggruppati().length;
  };

  // Reset filtri
  const resetFilters = () => {
    setFilters({
      searchText: '',
      clienteId: '',
      dataInizio: '',
      dataFine: ''
    });
  };

  // Conteggio filtri attivi
  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  // Export Excel fatture emesse
  const handleExportFatture = async () => {
    try {
      const filteredData = getFilteredFatture();
      
      const exportData = filteredData.map(item => ({
        'Protocollo OdL': item.ordine.protocollo_ordine_lavoro || '',
        'Cod. Progetto Cliente': item.ordine.codice_progetto_cliente || '',
        'Cliente': item.ordine.cliente_nome || '',
        'Progetto': item.ordine.nome_progetto || '',
        'Tipologia': item.ordine.tipologia_lavorazione || '',
        'Descrizione': item.ordine.descrizione_attivita || '',
        'SAL': `SAL ${item.numero_sal}`,
        'Percentuale': `${item.percentuale}%`,
        'Importo (€)': item.importo || 0,
        'OdA/Riferimento': item.riferimento_fattura || '',
        'N° Fattura': item.numero_fattura || '',
        'Data Fatturazione': item.data_fatturazione || '',
        'N° Ordine': item.ordine.numero_ordine || ''
      }));
      
      const response = await axios.post(`${API}/ordini-lavoro/export`, 
        { data: exportData },
        { 
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fatture_emesse_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export completato: ${filteredData.length} fatture esportate`);
    } catch (error) {
      console.error('Errore export:', error);
      toast.error('Errore durante l\'export');
    }
  };

  const handleGeneraFattura = (sal, ordine) => {
    setSelectedSAL(sal);
    setSelectedOrdine(ordine);
    setSelectedProgetto(null);
    setSelectedGruppoAccordo(null);
    
    const dataFatturaOggi = new Date().toISOString().split('T')[0];
    const dataScadenzaCalcolata = calcolaDataScadenza(
      dataFatturaOggi,
      sal.termini_pagamento_giorni || 30,
      sal.fattura_fine_mese || false
    );
    
    setFatturaData({
      numero_fattura: '',
      data_fattura: dataFatturaOggi,
      data_scadenza: dataScadenzaCalcolata,
      note: ''
    });
    setFatturaDialogOpen(true);
  };
  
  // Apre dialog per generare fattura accordo quadro
  const handleGeneraFatturaAccordo = (gruppo, importo) => {
    setSelectedGruppoAccordo(gruppo);
    setImportoManuale(importo || gruppo.importo_totale || '');
    setSelectedSAL(null);
    setSelectedOrdine(null);
    setSelectedProgetto(null);
    setFatturaData({
      numero_fattura: '',
      data_fattura: new Date().toISOString().split('T')[0],
      note: ''
    });
    setFatturaDialogOpen(true);
  };
  
  // Fattura un gruppo di progetti (accordo + mese)
  const handleFatturaGruppoProgetti = async (gruppo, numeroFattura) => {
    if (!numeroFattura) {
      toast.error('Inserisci il numero fattura');
      return;
    }
    
    try {
      const dataFattura = new Date().toISOString().split('T')[0];
      
      // Aggiorna tutti i progetti del gruppo
      const promises = gruppo.progetti.map(progetto => 
        axios.put(
          `${API}/progetti/${progetto.id}`,
          { 
            stato_fatturazione: 'fatturato',
            numero_fattura: numeroFattura,
            data_fattura: dataFattura
          },
          getAuthHeaders()
        )
      );
      
      await Promise.all(promises);
      
      const meseLabel = gruppo.mese === 'Non assegnato' ? gruppo.mese : new Date(gruppo.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      toast.success(`Fatturato ${gruppo.accordo_quadro_numero} - ${meseLabel} (${gruppo.progetti.length} progetti)`);
      fetchProgetti();
    } catch (error) {
      console.error('Errore fatturazione gruppo:', error);
      toast.error('Errore durante la fatturazione');
    }
  };

  const handleConfermaFattura = async () => {
    if (!fatturaData.numero_fattura) {
      toast.error('Inserisci il numero fattura');
      return;
    }

    try {
      // Se è un gruppo accordo quadro
      if (selectedGruppoAccordo) {
        const dataFattura = fatturaData.data_fattura;
        
        // Aggiorna tutti i progetti del gruppo
        const promises = selectedGruppoAccordo.progetti.map(progetto => 
          axios.put(
            `${API}/progetti/${progetto.id}`,
            { 
              stato_fatturazione: 'fatturato',
              numero_fattura: fatturaData.numero_fattura,
              data_fattura: dataFattura,
              data_scadenza_fattura: fatturaData.data_scadenza
            },
            getAuthHeaders()
          )
        );
        
        await Promise.all(promises);
        
        const meseLabel = selectedGruppoAccordo.mese === 'Non assegnato' ? selectedGruppoAccordo.mese : new Date(selectedGruppoAccordo.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
        toast.success(`Fatturato ${selectedGruppoAccordo.accordo_quadro_numero} - ${meseLabel}`);
        setFatturaDialogOpen(false);
        setSelectedGruppoAccordo(null);
        fetchProgetti();
        return;
      }
      
      // Se è un progetto da accordo quadro
      if (selectedProgetto) {
        await axios.put(
          `${API}/progetti/${selectedProgetto.id}`,
          { 
            stato_fatturazione: 'fatturato',
            numero_fattura: fatturaData.numero_fattura,
            data_fattura: fatturaData.data_fattura,
            data_scadenza_fattura: fatturaData.data_scadenza
          },
          getAuthHeaders()
        );
        toast.success('Progetto fatturato con successo!');
        setFatturaDialogOpen(false);
        setSelectedProgetto(null);
        fetchProgetti();
        return;
      }
      
      // Altrimenti è un SAL da ordine
      const updatedSalList = selectedOrdine.sal_list.map(s => 
        s.numero_sal === selectedSAL.numero_sal 
          ? { 
              ...s, 
              stato: 'fatturato', 
              numero_fattura: fatturaData.numero_fattura,
              data_fatturazione: fatturaData.data_fattura,
              data_scadenza: fatturaData.data_scadenza,
              note_fattura: fatturaData.note
            }
          : s
      );

      await axios.patch(
        `${API}/ordini-lavoro/${selectedOrdine.id}`,
        { sal_list: updatedSalList },
        getAuthHeaders()
      );

      toast.success('Fattura registrata con successo!');
      setFatturaDialogOpen(false);
      fetchOrdini();
    } catch (error) {
      console.error('Errore registrazione fattura:', error);
      toast.error('Errore nella registrazione della fattura');
    }
  };
  
  // Handler per aprire dialog fattura progetto
  const handleFatturaProgetto = (progetto) => {
    setSelectedProgetto(progetto);
    setSelectedSAL(null);
    setSelectedOrdine(null);
    setFatturaData({
      numero_fattura: '',
      data_fattura: new Date().toISOString().split('T')[0],
      data_scadenza: '',
      note: ''
    });
    setFatturaDialogOpen(true);
  };

  const salDaFatturare = getSALDaFatturare();
  const progettiDaFatturare = getProgettiDaFatturare();
  const gruppiAccordoDaFatturare = getProgettiRaggruppatiPerAccordoMese();
  const fattureEmesse = getFilteredFatture();
  const progettiFatturati = getProgettiFatturati();
  const gruppiAQFatturati = getProgettiFatturatiRaggruppati();
  
  const totaleFatturato = getSALFatturati().reduce((sum, item) => sum + (item.importo || 0), 0) + progettiFatturati.reduce((sum, p) => sum + (getCostoProgetto(p) || 0), 0);
  const totaleDaFatturare = salDaFatturare.reduce((sum, item) => sum + (item.importo || 0), 0) + progettiDaFatturare.reduce((sum, p) => sum + (p.importo_progetto || 0), 0);
  const totaleElementiDaFatturare = salDaFatturare.length + gruppiAccordoDaFatturare.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-3 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <DollarSign className="w-6 h-6 text-purple-600" />
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Fatturazione</h1>
            </div>
          </div>
          <div className="hidden sm:block text-right">
            <p className="text-sm text-slate-600">PaMa - Gestione SAL e Fatture</p>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Da Fatturare</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{totaleElementiDaFatturare}</p>
                <p className="text-xs text-slate-500">{salDaFatturare.length} SAL + {gruppiAccordoDaFatturare.length} Accordi QF</p>
                <p className="text-sm text-yellow-600 font-medium">{formatCurrency(totaleDaFatturare)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Fatture Emesse</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{getSALFatturati().length}</p>
                <p className="text-sm text-green-600 font-medium">{formatCurrency(totaleFatturato)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Totale Generale</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{formatCurrency(totaleFatturato + totaleDaFatturare)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('da_fatturare')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'da_fatturare'
                  ? 'bg-yellow-50 text-yellow-700 border-b-2 border-yellow-500'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <AlertCircle className="w-4 h-4 inline mr-2" />
              Da Fatturare ({totaleElementiDaFatturare})
            </button>
            <button
              onClick={() => setActiveTab('fatturate')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'fatturate'
                  ? 'bg-green-50 text-green-700 border-b-2 border-green-500'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <CheckCircle2 className="w-4 h-4 inline mr-2" />
              Fatture Emesse ({getSALFatturati().length + progettiFatturati.length})
            </button>
          </div>

          {/* TAB: Da Fatturare */}
          {activeTab === 'da_fatturare' && (
            <div className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-4 text-slate-600">Caricamento...</p>
                </div>
              ) : totaleElementiDaFatturare === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Tutto fatturato!</h3>
                  <p className="text-slate-600">Non ci sono elementi da fatturare al momento</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* SAL da Ordini */}
                  {salDaFatturare.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        SAL da Ordini di Lavoro ({salDaFatturare.length})
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Protocollo OdL</TableHead>
                            <TableHead className="font-semibold">Cod. Progetto Cliente</TableHead>
                            <TableHead className="font-semibold">Cliente</TableHead>
                            <TableHead className="font-semibold">Progetto</TableHead>
                            <TableHead className="font-semibold">SAL</TableHead>
                            <TableHead className="font-semibold">Percentuale</TableHead>
                            <TableHead className="font-semibold">Importo</TableHead>
                            <TableHead className="font-semibold text-orange-600">OdA/Riferimento</TableHead>
                            <TableHead className="text-right font-semibold">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {salDaFatturare.map((item, index) => (
                            <TableRow 
                              key={`${item.ordine.id}-${item.numero_sal}`}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleGeneraFattura(item, item.ordine)}
                      >
                        <TableCell className="font-mono font-semibold text-blue-600">
                          {item.ordine.protocollo_ordine_lavoro || '-'}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {item.ordine.codice_progetto_cliente || '-'}
                        </TableCell>
                        <TableCell className="font-medium">{item.ordine.cliente_nome}</TableCell>
                        <TableCell className="text-sm">{item.ordine.nome_progetto}</TableCell>
                        <TableCell>
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                            SAL {item.numero_sal}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{item.percentuale}%</TableCell>
                        <TableCell className="font-bold text-lg text-purple-600">
                          {formatCurrency(item.importo)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.riferimento_fattura ? (
                            <span className="text-orange-600 font-medium">{item.riferimento_fattura}</span>
                          ) : (
                            <span className="text-slate-400 italic">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGeneraFattura(item, item.ordine);
                            }}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Genera Fattura
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                    </div>
                  )}
                  
                  {/* Progetti da Accordi Quadro - Raggruppati per Accordo + Mese */}
                  {getProgettiRaggruppatiPerAccordoMese().length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        Accordi Quadro da Fatturare ({getProgettiRaggruppatiPerAccordoMese().length} voci)
                      </h3>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-purple-50">
                            <TableHead className="font-semibold">N° Accordo</TableHead>
                            <TableHead className="font-semibold">Codice Accordo Quadro</TableHead>
                            <TableHead className="font-semibold">Cliente</TableHead>
                            <TableHead className="font-semibold">Mese Riferimento</TableHead>
                            <TableHead className="font-semibold">Importo</TableHead>
                            <TableHead className="font-semibold text-orange-600">Codice OdA</TableHead>
                            <TableHead className="text-right font-semibold">Azioni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getProgettiRaggruppatiPerAccordoMese().map((gruppo, idx) => (
                            <TableRow 
                              key={`${gruppo.accordo_quadro_id}_${gruppo.mese}`}
                              className="hover:bg-purple-50"
                            >
                              <TableCell>
                                <span className="font-mono text-purple-600 font-semibold">{gruppo.accordo_quadro_numero}</span>
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-sm text-slate-600">{gruppo.codice_accordo_quadro || '-'}</span>
                              </TableCell>
                              <TableCell className="font-medium">{gruppo.cliente_nome}</TableCell>
                              <TableCell>
                                <span className="px-2 py-1 rounded bg-purple-100 text-purple-700 text-sm font-medium">
                                  {gruppo.mese === 'Non assegnato' ? gruppo.mese : new Date(gruppo.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  placeholder="Importo €"
                                  defaultValue={gruppo.importo_totale || ''}
                                  className="w-28 h-8 text-sm font-semibold"
                                  id={`importo-${idx}`}
                                />
                              </TableCell>
                              <TableCell>
                                {gruppo.codice_oda ? (
                                  <span className="font-mono text-sm bg-orange-50 text-orange-700 px-2 py-1 rounded">
                                    {gruppo.codice_oda}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 italic">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  className="bg-purple-600 hover:bg-purple-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const importo = document.getElementById(`importo-${idx}`)?.value;
                                    handleGeneraFatturaAccordo(gruppo, importo);
                                  }}
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Genera Fattura
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB: Fatture Emesse */}
          {activeTab === 'fatturate' && (
            <div className="p-6">
              {/* Barra di ricerca e filtri */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Barra di ricerca */}
                  <div className="relative flex-1 min-w-[250px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      placeholder="Cerca per ordine, cliente, progetto, riferimento..."
                      value={filters.searchText}
                      onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                      className="pl-10"
                      data-testid="search-fatture"
                    />
                  </div>
                  
                  {/* Pulsante Filtri */}
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? "bg-green-600 hover:bg-green-700" : ""}
                    data-testid="toggle-filters-fatture"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filtri
                    {activeFiltersCount > 0 && (
                      <span className="ml-2 bg-white text-green-600 px-2 py-0.5 rounded-full text-xs font-bold">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                  
                  {/* Export Excel */}
                  <Button
                    variant="outline"
                    onClick={handleExportFatture}
                    disabled={fattureEmesse.length === 0}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    data-testid="export-fatture"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Excel
                  </Button>
                  
                  {/* Reset filtri */}
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      onClick={resetFilters}
                      className="text-slate-500 hover:text-slate-700"
                      data-testid="reset-filters-fatture"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  )}
                  
                  {/* Conteggio risultati */}
                  <span className="text-sm text-slate-500">
                    {fattureEmesse.length} di {getTotaleFattureCount()} fatture
                  </span>
                </div>
                
                {/* Pannello filtri espandibile */}
                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-200">
                    {/* Filtro Cliente */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Cliente</Label>
                      <Select
                        value={filters.clienteId}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, clienteId: value === 'all' ? '' : value }))}
                      >
                        <SelectTrigger data-testid="filter-cliente-fatture">
                          <SelectValue placeholder="Tutti i clienti" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti i clienti</SelectItem>
                          {clienti.map(cliente => (
                            <SelectItem key={cliente.id} value={cliente.id}>
                              {cliente.nome_azienda}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Filtro Date */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Data Fatturazione da</Label>
                      <Input
                        type="date"
                        value={filters.dataInizio}
                        onChange={(e) => setFilters(prev => ({ ...prev, dataInizio: e.target.value }))}
                        data-testid="filter-data-inizio-fatture"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Data Fatturazione a</Label>
                      <Input
                        type="date"
                        value={filters.dataFine}
                        onChange={(e) => setFilters(prev => ({ ...prev, dataFine: e.target.value }))}
                        data-testid="filter-data-fine-fatture"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Tabella Fatture Emesse */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                  <p className="mt-4 text-slate-600">Caricamento...</p>
                </div>
              ) : fattureEmesse.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {getSALFatturati().length === 0 ? 'Nessuna fattura emessa' : 'Nessun risultato'}
                  </h3>
                  <p className="text-slate-600">
                    {getSALFatturati().length === 0 
                      ? 'Non ci sono ancora fatture emesse' 
                      : 'Nessuna fattura corrisponde ai filtri selezionati'}
                  </p>
                  {activeFiltersCount > 0 && (
                    <Button variant="outline" onClick={resetFilters} className="mt-4">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Rimuovi filtri
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Protocollo OdL</TableHead>
                      <TableHead className="font-semibold">Cod. Progetto Cliente</TableHead>
                      <TableHead className="font-semibold">Cliente</TableHead>
                      <TableHead className="font-semibold">Progetto</TableHead>
                      <TableHead className="font-semibold">Tipologia</TableHead>
                      <TableHead className="font-semibold">Tipo</TableHead>
                      <TableHead className="font-semibold">Importo</TableHead>
                      <TableHead className="font-semibold text-orange-600">OdA/Riferimento</TableHead>
                      <TableHead className="font-semibold text-purple-600">N° Fattura</TableHead>
                      <TableHead className="font-semibold">Data Fatt.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fattureEmesse.map((item, index) => (
                      <TableRow 
                        key={`${item.ordine.id}-${item.numero_sal}-${index}`}
                        className={`hover:bg-slate-50 ${item.tipo === 'accordo_quadro' ? 'bg-purple-50/30' : ''}`}
                      >
                        <TableCell className="font-mono font-semibold text-blue-600">
                          {item.ordine.protocollo_ordine_lavoro || '-'}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {item.ordine.codice_progetto_cliente || '-'}
                        </TableCell>
                        <TableCell className="font-medium">{item.ordine.cliente_nome}</TableCell>
                        <TableCell className="text-sm">{item.ordine.nome_progetto}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {item.ordine.tipologia_lavorazione || '-'}
                        </TableCell>
                        <TableCell>
                          {item.tipo === 'accordo_quadro' ? (
                            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                              AQ
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                              SAL {item.numero_sal}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`font-bold ${item.tipo === 'accordo_quadro' ? 'text-purple-600' : 'text-green-600'}`}>
                          {formatCurrency(item.importo)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.riferimento_fattura ? (
                            <span className="text-orange-600 font-medium">{item.riferimento_fattura}</span>
                          ) : (
                            <span className="text-slate-400 italic">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.numero_fattura ? (
                            <span className="text-purple-600 font-semibold">{item.numero_fattura}</span>
                          ) : (
                            <span className="text-slate-400 italic">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {item.data_fatturazione || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {/* Totale */}
              {fattureEmesse.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
                  <div className="bg-green-50 px-6 py-3 rounded-lg">
                    <span className="text-sm text-slate-600 mr-4">Totale Fatturato:</span>
                    <span className="text-xl font-bold text-green-600">
                      {formatCurrency(fattureEmesse.reduce((sum, item) => sum + (item.importo || 0), 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialog Genera Fattura */}
      <Dialog open={fatturaDialogOpen} onOpenChange={setFatturaDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedGruppoAccordo 
                ? `Genera Fattura - ${selectedGruppoAccordo.accordo_quadro_numero}`
                : selectedProgetto 
                  ? `Genera Fattura - Progetto ${selectedProgetto.numero_progetto}` 
                  : `Genera Fattura - SAL ${selectedSAL?.numero_sal}`}
            </DialogTitle>
          </DialogHeader>

          {/* Fattura per Gruppo Accordo Quadro */}
          {selectedGruppoAccordo && (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Accordo Quadro:</span>
                  <span className="font-semibold">{selectedGruppoAccordo.accordo_quadro_numero}</span>
                </div>
                {selectedGruppoAccordo.codice_accordo_quadro && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Codice:</span>
                    <span className="font-mono text-sm">{selectedGruppoAccordo.codice_accordo_quadro}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">Cliente:</span>
                  <span className="font-semibold">{selectedGruppoAccordo.cliente_nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Mese:</span>
                  <span className="font-medium">
                    {selectedGruppoAccordo.mese === 'Non assegnato' ? selectedGruppoAccordo.mese : new Date(selectedGruppoAccordo.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                  </span>
                </div>
                {selectedGruppoAccordo.codice_oda && (
                  <div className="flex justify-between pt-2 border-t border-purple-200">
                    <span className="text-orange-600">Codice OdA:</span>
                    <span className="font-semibold text-orange-600">{selectedGruppoAccordo.codice_oda}</span>
                  </div>
                )}
              </div>

              {/* Form Fattura */}
              <div className="space-y-3">
                <div>
                  <Label>Importo da Fatturare (€) *</Label>
                  <Input
                    type="number"
                    value={importoManuale}
                    onChange={(e) => setImportoManuale(e.target.value)}
                    placeholder="Inserisci importo"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Numero Fattura *</Label>
                  <Input
                    value={fatturaData.numero_fattura}
                    onChange={(e) => setFatturaData(prev => ({ ...prev, numero_fattura: e.target.value }))}
                    placeholder="Es: FT-2024-001"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Data Fattura</Label>
                  <Input
                    type="date"
                    value={fatturaData.data_fattura}
                    onChange={(e) => setFatturaData(prev => ({ ...prev, data_fattura: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setFatturaDialogOpen(false); setSelectedGruppoAccordo(null); }}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleConfermaFattura}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Conferma Fattura
                </Button>
              </div>
            </div>
          )}

          {/* Fattura per Progetto Accordo Quadro */}
          {selectedProgetto && (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Progetto:</span>
                  <span className="font-semibold">{selectedProgetto.numero_progetto}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Accordo Quadro:</span>
                  <span className="font-semibold">{selectedProgetto.accordo_quadro_numero}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Cliente:</span>
                  <span className="font-semibold">{selectedProgetto.cliente_nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Nome:</span>
                  <span className="font-medium">{selectedProgetto.nome_progetto}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-purple-200">
                  <span className="text-slate-500">Importo Progetto</span>
                  <span className="font-bold text-xl text-purple-600">
                    {formatCurrency(selectedProgetto.importo_progetto)}
                  </span>
                </div>
                {selectedProgetto.codice_oda && (
                  <div className="flex justify-between pt-2 border-t border-purple-200">
                    <span className="text-orange-600">Codice OdA:</span>
                    <span className="font-semibold text-orange-600">{selectedProgetto.codice_oda}</span>
                  </div>
                )}
              </div>

              {/* Form Fattura */}
              <div className="space-y-3">
                <div>
                  <Label>Numero Fattura *</Label>
                  <Input
                    value={fatturaData.numero_fattura}
                    onChange={(e) => setFatturaData(prev => ({ ...prev, numero_fattura: e.target.value }))}
                    placeholder="Es: FT-2024-001"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Data Fattura</Label>
                  <Input
                    type="date"
                    value={fatturaData.data_fattura}
                    onChange={(e) => setFatturaData(prev => ({ ...prev, data_fattura: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setFatturaDialogOpen(false); setSelectedProgetto(null); }}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleConfermaFattura}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Conferma Fattura
                </Button>
              </div>
            </div>
          )}

          {/* Fattura per SAL Ordine */}
          {selectedSAL && selectedOrdine && (
            <div className="space-y-4">
              {/* Info da copiare */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                <p className="text-xs text-blue-600 font-medium mb-2">📋 Clicca sui valori per copiarli</p>
                
                <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                     onClick={() => copyToClipboard(selectedOrdine.protocollo_ordine_lavoro || selectedOrdine.numero_ordine, 'Protocollo Ordine di Lavoro')}>
                  <span className="text-slate-600">Protocollo Ordine di Lavoro:</span>
                  <span className="font-semibold text-blue-700">{selectedOrdine.protocollo_ordine_lavoro || selectedOrdine.numero_ordine}</span>
                </div>
                
                <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                     onClick={() => copyToClipboard(selectedOrdine.codice_progetto_cliente || '-', 'Codice Progetto Cliente')}>
                  <span className="text-slate-600">Cod. Progetto Cliente:</span>
                  <span className="font-mono text-sm">{selectedOrdine.codice_progetto_cliente || '-'}</span>
                </div>
                
                <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                     onClick={() => copyToClipboard(selectedOrdine.cliente_nome, 'Cliente')}>
                  <span className="text-slate-600">Cliente:</span>
                  <span className="font-semibold">{selectedOrdine.cliente_nome}</span>
                </div>
                
                <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                     onClick={() => copyToClipboard(selectedOrdine.nome_progetto || '-', 'Nome Progetto')}>
                  <span className="text-slate-600">Nome Progetto:</span>
                  <span className="font-medium text-sm">{selectedOrdine.nome_progetto || '-'}</span>
                </div>
                
                {selectedOrdine.tipologia_lavorazione && (
                  <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                       onClick={() => copyToClipboard(selectedOrdine.tipologia_lavorazione, 'Tipologia Lavorazione')}>
                    <span className="text-slate-600">Tipologia Lavorazione:</span>
                    <span className="font-medium text-sm">{selectedOrdine.tipologia_lavorazione}</span>
                  </div>
                )}
                
                {selectedOrdine.descrizione_attivita && (
                  <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                       onClick={() => copyToClipboard(selectedOrdine.descrizione_attivita, 'Descrizione Attività')}>
                    <span className="text-slate-600">Descrizione Attività:</span>
                    <span className="font-medium text-sm">{selectedOrdine.descrizione_attivita}</span>
                  </div>
                )}
                
                {selectedOrdine.riferimento_ordine_acquisto && (
                  <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                       onClick={() => copyToClipboard(selectedOrdine.riferimento_ordine_acquisto, 'OdA')}>
                    <span className="text-orange-600">OdA:</span>
                    <span className="font-semibold text-orange-600">{selectedOrdine.riferimento_ordine_acquisto}</span>
                  </div>
                )}
                
                {selectedSAL.riferimento_fattura && (
                  <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                       onClick={() => copyToClipboard(selectedSAL.riferimento_fattura, 'Riferimento Fattura')}>
                    <span className="text-orange-600">Rif. Fattura/OdA:</span>
                    <span className="font-semibold text-orange-600">{selectedSAL.riferimento_fattura}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center hover:bg-blue-100 p-1 rounded cursor-pointer"
                     onClick={() => copyToClipboard(selectedSAL.descrizione || `SAL ${selectedSAL.numero_sal} - ${selectedSAL.percentuale}%`, 'Descrizione SAL')}>
                  <span className="text-slate-600">Descrizione SAL:</span>
                  <span className="font-medium text-sm">{selectedSAL.descrizione || `SAL ${selectedSAL.numero_sal} - ${selectedSAL.percentuale}%`}</span>
                </div>
                
                <div className="flex justify-between pt-2 border-t border-blue-200">
                  <span className="text-slate-500">Importo SAL</span>
                  <span className="font-bold text-xl text-purple-600">
                    {formatCurrency(selectedSAL.importo)}
                  </span>
                </div>
                
                {/* Termini di Pagamento */}
                <div className="flex justify-between pt-2 border-t border-blue-200">
                  <span className="text-slate-500">Termini Pagamento</span>
                  <span className="font-medium text-slate-700">
                    {selectedSAL.termini_pagamento_giorni || 30} giorni
                    {selectedSAL.fattura_fine_mese && (
                      <span className="ml-1 text-blue-600 text-sm">(da fine mese)</span>
                    )}
                  </span>
                </div>
              </div>
              
              {/* Riepilogo Fatture già emesse per questo ordine */}
              {selectedOrdine.sal_list && selectedOrdine.sal_list.some(s => s.stato === 'fatturato') && (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-700 mb-2">📋 Fatture già emesse per questo ordine:</p>
                  <div className="space-y-2">
                    {selectedOrdine.sal_list
                      .filter(s => s.stato === 'fatturato')
                      .map((sal, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm bg-white p-2 rounded border border-green-200">
                          <div>
                            <span className="font-medium">SAL {sal.numero_sal}</span>
                            {sal.descrizione && <span className="text-slate-500 ml-2">- {sal.descrizione}</span>}
                          </div>
                          <div className="text-right">
                            <span className="font-semibold text-green-600">{formatCurrency(sal.importo)}</span>
                            {sal.numero_fattura && (
                              <span className="text-xs text-slate-500 ml-2">({sal.numero_fattura})</span>
                            )}
                          </div>
                        </div>
                      ))}
                    <div className="flex justify-between pt-2 border-t border-green-300 text-sm">
                      <span className="font-medium text-green-700">Totale già fatturato:</span>
                      <span className="font-bold text-green-700">
                        {formatCurrency(selectedOrdine.sal_list
                          .filter(s => s.stato === 'fatturato')
                          .reduce((sum, s) => sum + (s.importo || 0), 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Fattura */}
              <div className="space-y-3">
                <div>
                  <Label>Numero Fattura *</Label>
                  <Input
                    value={fatturaData.numero_fattura}
                    onChange={(e) => setFatturaData(prev => ({ ...prev, numero_fattura: e.target.value }))}
                    placeholder="Es: FT-2024-001"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Data Fattura</Label>
                    <Input
                      type="date"
                      value={fatturaData.data_fattura}
                      onChange={(e) => handleDataFatturaChange(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Data Scadenza *</Label>
                    <Input
                      type="date"
                      value={fatturaData.data_scadenza}
                      onChange={(e) => setFatturaData(prev => ({ ...prev, data_scadenza: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Note (opzionale)</Label>
                  <Input
                    value={fatturaData.note}
                    onChange={(e) => setFatturaData(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="Note aggiuntive..."
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setFatturaDialogOpen(false)}>
                  Annulla
                </Button>
                <Button 
                  onClick={handleConfermaFattura}
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={!fatturaData.data_scadenza}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Conferma Fattura
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FatturazionePage;
